import { CountryCode, getCountries, parsePhoneNumberFromString } from 'libphonenumber-js';

/**
 * Phone numbers are stored bare (E.164, `+31612345678`) and formatted here (PAUL-3440). The `+`
 * and the country code make the value self-describing, so nothing has to be told which country a
 * number belongs to.
 *
 * Values written before the change are rewritten by a one-off backfill per environment, and the
 * few it cannot read (a literal `Nederland`) stay as they were. So treat every phone as *probably*
 * E.164, never as certainly: format it when it parses, show it as stored when it does not.
 */

const DEFAULT_COUNTRY: CountryCode = 'NL';

/**
 * `shop.country` / `me.country` are stored strings - they can be stale, blank or lowercase. An
 * unusable one is not an error to libphonenumber: it silently fails to parse every *national*
 * number, so `0612345678` would come back raw with no clue why. Normalize instead.
 */
export const homeCountryOf = (country?: string | null): CountryCode => {
  const code = country?.trim().toUpperCase();
  return code && getCountries().includes(code as CountryCode) ? (code as CountryCode) : DEFAULT_COUNTRY;
};

const parse = (value: string | null | undefined, country?: string | null) =>
  value?.trim() ? parsePhoneNumberFromString(value.trim(), homeCountryOf(country)) : undefined;

/** The number as a reader in `homeCountry` expects to see it; unparseable values pass through. */
export function formatPhone(value: string | null | undefined, homeCountry?: string | null): string {
  if (!value) return '';

  const parsed = parse(value, homeCountry);
  if (!parsed) return value;

  return parsed.country === homeCountryOf(homeCountry) ? parsed.formatNational() : parsed.formatInternational();
}

/**
 * What to put on the wire. E.164 when the value parses; otherwise the trimmed text, because the
 * backend names the exact problem (`phone_too_short`, `phone_invalid_for_country`, ...) in copy
 * better than anything we can guess here.
 *
 * `storedValue` keeps the backend's echo rule intact: a value the operator did not touch goes back
 * byte-identical, so it is never judged. Normalizing it instead would turn a legacy row into a save
 * that fails in Strict mode - `06123456` is a possible length, so it parses to `+316123456` and
 * arrives as a *changed* value the backend then refuses as an unallocated range. Pass it from every
 * form that prefills a stored number, especially the full-replace ones where an untouched field
 * rides along with a save of something else.
 */
export function toWirePhone(
  value: string | null | undefined,
  homeCountry?: string | null,
  storedValue?: string | null
): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  const stored = storedValue?.trim();
  if (stored && trimmed === stored) return storedValue.trim();

  return parse(trimmed, homeCountry)?.number ?? trimmed;
}

/**
 * Whether two stored values are the same number. Compares the parsed form, so a row the backfill
 * has reached and one it has not (`+31612345678` vs `06 12345678`) still count as one number.
 */
export function samePhone(a: string | null | undefined, b: string | null | undefined, homeCountry?: string | null) {
  const left = a?.trim();
  const right = b?.trim();
  if (!left || !right) return false;

  return (parse(left, homeCountry)?.number ?? left) === (parse(right, homeCountry)?.number ?? right);
}
